import { atom } from 'jotai';

import { IMeasurement, isSemanticallyEmpty, Segmentation } from '@core';

import { activeAnalysisAtom, comparisonAnalysisAtom } from './swings';

const SWING_PLANE_PARAM_NAMES = [
    'swing_plane_0',
    'swing_plane_1',
    'swing_plane_2',
    'swing_plane_3',
    'swing_plane_4',
    'swing_plane_5',
] as const;

export type RawSkeleton = { [boneId: string]: [number[], number[], number[]] };
export type SwingPlane = [number, number, number, number, number, number];
export type SkeletonFrame = { [keypointId: string]: [number, number, number] };
export type FrameFirstSkeleton = SkeletonFrame[];
export type FrameFirstClubTrack = {
    shaft_start_coordinates: [number, number, number][];
    shaft_end_coordinates: [number, number, number][];
};
export type ClubTrack = {
    shaft_start_coordinates: [number[], number[], number[]];
    shaft_end_coordinates: [number[], number[], number[]];
};

type Axes = readonly [number[], number[], number[]];

function axisFirstToFrameFirst([x, y, z]: Axes): [number, number, number][] {
    return x.map((_, i) => [x[i], y[i], z[i]]);
}

export function transformSkeletonToFramesFirst(skeleton: RawSkeleton): FrameFirstSkeleton {
    const perKeypoint = Object.entries(skeleton).map(([id, axes]) => [id, axisFirstToFrameFirst(axes)] as const);
    const numFrames = perKeypoint[0]?.[1].length ?? 0;

    return Array.from({ length: numFrames }, (_, i) =>
        Object.fromEntries(perKeypoint.map(([id, frames]) => [id, frames[i]])),
    );
}

export function transformClubTrackToFramesFirst(clubTrack?: ClubTrack): FrameFirstClubTrack {
    return {
        shaft_start_coordinates: clubTrack ? axisFirstToFrameFirst(clubTrack.shaft_start_coordinates) : [],
        shaft_end_coordinates: clubTrack ? axisFirstToFrameFirst(clubTrack.shaft_end_coordinates) : [],
    };
}

export type Data3d = {
    skeleton: FrameFirstSkeleton;
    clubTrack: FrameFirstClubTrack;
    bones: {
        start_point_id: number;
        end_point_id: number;
    }[];
    segmentation: Segmentation;
    keypointIdToName: Record<string, string>;
    measurements: IMeasurement[] | undefined;
    golfBallTranslation: [number, number, number] | undefined;
};

export const activeData3dAtom = atom<Data3d | null>((get) => {
    const analysisResp = get(activeAnalysisAtom);
    const analysis = analysisResp?.fullAnalysis ?? analysisResp?.quickAnalysis;
    if (isSemanticallyEmpty(analysis?.data) || !analysis?.data.analysis || analysis.data.analysis.isQuickAnalysis) {
        return null;
    }

    const {
        skeleton: skeletonData,
        club_track,
        bones,
        segmentation,
        keypoint_id_to_name: keypointIdToName,
        measurements,
    } = analysis.data.analysis ?? {};

    return {
        skeleton: transformSkeletonToFramesFirst(skeletonData ?? {}),
        clubTrack: transformClubTrackToFramesFirst(club_track),
        bones,
        segmentation,
        keypointIdToName,
        measurements,
        golfBallTranslation: analysisResp?.metadata.golfBallTranslation,
    };
});

export const activeSwingPlaneAtom = atom((get) => {
    const analysisResp = get(activeAnalysisAtom);
    const analysis = analysisResp?.fullAnalysis ?? analysisResp?.quickAnalysis;
    const parametersMap = analysis?.data.analysis?.parameter_values;
    if (!analysisResp || !parametersMap || !SWING_PLANE_PARAM_NAMES.every((key) => !!parametersMap[key])) {
        return;
    }
    return SWING_PLANE_PARAM_NAMES.map((key) => parametersMap[key].value ?? 0) as SwingPlane;
});

export const comparisonData3dAtom = atom<Data3d | null>((get) => {
    const analysisResp = get(comparisonAnalysisAtom);
    const analysis = analysisResp?.fullAnalysis ?? analysisResp?.quickAnalysis;
    if (isSemanticallyEmpty(analysis?.data) || !analysis?.data.analysis || analysis.data.analysis.isQuickAnalysis) {
        return null;
    }

    const {
        skeleton: skeletonData,
        club_track,
        bones,
        segmentation,
        keypoint_id_to_name: keypointIdToName,
        measurements,
    } = analysis.data.analysis ?? {};

    return {
        skeleton: transformSkeletonToFramesFirst(skeletonData ?? {}),
        clubTrack: transformClubTrackToFramesFirst(club_track),
        bones,
        segmentation,
        keypointIdToName,
        measurements,
        golfBallTranslation: analysisResp?.metadata.golfBallTranslation,
    };
});

export const comparisonSwingPlaneAtom = atom((get) => {
    const analysisResp = get(comparisonAnalysisAtom);
    const analysis = analysisResp?.fullAnalysis ?? analysisResp?.quickAnalysis;
    const parametersMap = analysis?.data.analysis?.parameter_values;
    if (!analysisResp || !parametersMap || !SWING_PLANE_PARAM_NAMES.every((key) => !!parametersMap[key])) {
        return;
    }
    return SWING_PLANE_PARAM_NAMES.map((key) => parametersMap[key].value ?? 0) as SwingPlane;
});
